/* eslint-disable no-param-reassign */
import { withFormik } from 'formik';
import {
  channelAfterUpdate,
  initialIsOptedOut,
  shouldRemoveChannelAfterUpdate,
} from 'components/@home/drawers/ChannelDrawer/Form/superAdminChannelEdit';
import Group from 'services/api/Channel';
import { I18n } from 'utils/i18n';
import * as Yup from 'yup';

class ChannelSubmit {
  constructor(values, formikBag) {
    this.formikBag = formikBag;
    this.values = values;
  }

  assembleForm = () => {
    const {
      members,
      membersAdded,
      membersRemoved,
      groupTeams,
      groupTeamsRemoved,
      isMuteable,
      isReadOnly,
      isOptedOut,
      name,
      description,
      preview,
    } = this.values;
    const { authEmployee, isSuperAdmin } = this.formikBag.props;

    const membersWithAdmin = (preview ? members : membersAdded).map(m => ({
      employee: m._id,
      isAdmin: m.groupRole === 'admin' ? 1 : 0,
      canWriteAsChannel: m.groupRole === 'writer' ? 1 : 0,
    }));
    if (!isSuperAdmin && (isReadOnly[0] === 'on' || isOptedOut[0] !== 'on')) {
      membersWithAdmin.push({
        employee: authEmployee._id,
        isAdmin: 1,
        canWriteAsChannel: 0,
      });
    } else if (!isSuperAdmin && isOptedOut[0] === 'on') {
      membersRemoved.push(authEmployee);
    }

    return {
      name,
      description,
      members: membersWithAdmin,
      groupTeams: groupTeams.map(gt => ({
        team: gt.value._id,
        isAdmin: gt.value.groupRole === 'admin' ? 1 : 0,
        canWriteAsChannel: gt.value.groupRole === 'writer' ? 1 : 0,
      })),
      groupTeamsRemoved,
      membersRemoved: membersRemoved.map(e => ({ employee: e._id, user: e.user?._id })),
      isActive: 1,
      isMuteable: isMuteable[0] === 'on' ? 1 : 0,
      isReadOnly: isReadOnly[0] === 'on' ? 1 : 0,
    };
  };

  sendRequest = (form, preview) => {
    const {
      props: { channel, isEdit },
    } = this.formikBag;
    if (preview) {
      return Group.updatePreview(isEdit ? channel._id : 'new', form).then(data => {
        if (data.members) {
          this.formikBag.setFieldValue(
            'members',
            data.members.map(({ employee: _id, ...m }) => ({ _id, ...m })),
          );
          this.formikBag.setFieldValue('preview', false);
        }
      });
    }
    if (isEdit) {
      return Group.update(channel._id, form);
    }
    return Group.create(form);
  };

  uploadAvatar = async (channelId, actions, channelSnapshot) => {
    const { setSubmitting, setErrors } = this.formikBag;
    const { avatar } = this.values;
    // Existing signed URL strings must not be re-POSTed as multipart (triggers loops).
    if (!(avatar instanceof File || avatar instanceof Blob)) {
      return;
    }

    const previewUrl = URL.createObjectURL(avatar);
    actions.channels.set({ ...channelSnapshot, avatar: previewUrl });

    try {
      await Group.upload(channelId, avatar);
      // Identity worker returns 202 with the pre-upload group; keep the blob until channelUpdated
      // delivers a signed avatar URL. Legacy multipart upload also emits channelUpdated.
    } catch {
      URL.revokeObjectURL(previewUrl);
      actions.channels.set(channelSnapshot);
      setSubmitting(false);
      setErrors({ server: I18n.t('Picture was not uploaded for some reason') });
    }
  };

  displayErrors = err => {
    const { name } = this.values;
    const { setErrors } = this.formikBag;

    console.error(err);
    if (err.status === 409) {
      setErrors({ server: `Channel "${name}" already exists` });
    } else {
      setErrors({ server: I18n.t('Something went wrong with sending data') });
    }
  };
}

export default withFormik({
  validationSchema: Yup.object().shape({
    name: Yup.string()
      .max(50, () => I18n.t('ChannelDrawer.Should not exceed', { count: 50 }))
      .trim()
      .required(() => I18n.t('ChannelDrawer.Please enter name for the channel')),

    description: Yup.string()
      .max(200, () => I18n.t('ChannelDrawer.Should not exceed', { count: 200 }))
      .trim(),

    isOptedOut: Yup.array().max(1),
  }),

  mapPropsToValues: ({ channel, isSuperAdmin, authEmployee: ae, isEdit }) => {
    if (!channel) isEdit = false;
    const isDirectMember = channel?.employees.some(m => m._id === ae?._id);
    const fromTeams = !!channel?.employees.find(m => m._id === ae?._id)?.fromTeams?.length > 0;
    const ret = {
      _id: isEdit ? channel._id : undefined,
      name: isEdit ? channel.name : '',
      description: isEdit ? channel.description : '',
      isMuteable: isEdit && channel.isMuteable ? ['on'] : [],
      isReadOnly: isEdit && channel.isReadOnly ? ['on'] : [],
      members: isEdit
        ? channel.employees
            .filter(e => e._id !== ae?._id)
            .map(e => ({
              _id: e._id,
              groupRole: e.groupRole,
              fromTeams: e.fromTeams,
              createdFromTeams: e.createdFromTeams,
              channelRoleManualOverride: e.channelRoleManualOverride,
            }))
        : [],
      me: isEdit ? channel.employees.filter(e => e._id === ae?._id)[0] : null,
      groupTeams: isEdit
        ? channel.groupTeams?.map(gt => ({
            label: gt.name,
            value: {
              _id: gt.team,
              name: gt.name,
              groupRole: gt.groupRole,
            },
          })) || []
        : [],
      groupTeamsRemoved: [],
      membersAdded: [],
      membersRemoved: [],
      optOutDisabled:
        isSuperAdmin ||
        !ae?.isAdmin ||
        fromTeams ||
        (isEdit && channel.isMember && !isDirectMember),
      isOptedOut: initialIsOptedOut({
        isEdit,
        isSuperAdmin,
        isMember: !!channel?.isMember,
      }),
      avatar: isEdit ? channel.avatar : '',
      isAdmin: isEdit ? channel.isAdmin : true,
    };
    return ret;
  },

  handleSubmit: async (values, formikBag) => {
    const {
      props: { actions, isEdit, isSuperAdmin, navigate, channel: previousChannel },
      setSubmitting,
    } = formikBag;

    const handler = new ChannelSubmit(values, formikBag);

    const form = handler.assembleForm();

    try {
      const rawChannel = await handler.sendRequest(form, values.preview);
      if (!values.preview) {
        let updatedChannel = channelAfterUpdate(rawChannel, isSuperAdmin);
        // PATCH /group does not update avatar (identity worker does); keep the store URL until blob/WS.
        if (previousChannel?.avatar) {
          updatedChannel = { ...updatedChannel, avatar: previousChannel.avatar };
        }
        const removeFromList = shouldRemoveChannelAfterUpdate({
          isMember: !!rawChannel.isMember,
          isSuperAdmin,
        });
        if (removeFromList) {
          actions.channels.remove(rawChannel._id);
        } else {
          actions.channels.set(updatedChannel);
        }

        if (removeFromList) {
          setTimeout(() => {
            navigate('/home/messages/');
          }, 200);
        } else if (!isEdit) {
          setTimeout(() => navigate(`/home/messages/${rawChannel._id}`), 200);
        }

        if (!removeFromList) {
          await handler.uploadAvatar(rawChannel._id, actions, updatedChannel);
        }

        actions.drawers.channel.close();
      }
      setSubmitting(false);
    } catch (err) {
      setSubmitting(false);
      handler.displayErrors(err);
    }
  },
  displayName: 'ChannelForm',
});
